
import { useCallback } from 'react';
import { useVezgoState } from '@/providers/vezgo';
import { logger } from '@/services/LoggingService';
import { ConnectionProcessingState } from '@/providers/vezgo/constants';
import { handleVezgoError } from '@/providers/vezgo/utils/error-handler';
import { toast } from 'sonner';
import { buildVezgoConnectUrl } from '@/providers/vezgo/utils/redirect';
import { vezgoConfig } from '@/config';
import { generateRandomState } from '@/providers/vezgo/utils/state';
import { tokenService } from '@/providers/vezgo/services/TokenService';

export function useVezgoConnectActions() {
  const vezgoState = useVezgoState();

  /**
   * Connect handler—ensures token fetch & handles errors in stepwise manner
   */
  const handleConnect = useCallback(async () => {
    logger.info('useVezgoConnectActions', 'Initiating connect action');
    vezgoState.startProcessing();
    vezgoState.setProcessingState(ConnectionProcessingState.CONNECTING);

    const toastId = toast.loading("Fetching API token...");

    try {
      // Always generate a fresh STATE for this session
      const state = generateRandomState();

      // ** Enforce: Get token right before connecting **
      const token = await tokenService.getToken(vezgoConfig.clientId);

      // Add detailed token debug logging
      logger.info('useVezgoConnectActions', 'connect: tokenService.getToken call returned', {
        tokenPresent: !!token,
        tokenLength: token ? token.length : 0
      });

      if (!token || typeof token !== "string" || (token?.length || 0) < 15) {
        logger.error('useVezgoConnectActions', 'Failed to acquire valid token', {
          tokenSample: token ? token.slice(0, 8) : null
        });
        toast.error("Failed to obtain API token for Vezgo connect. Please try again.", { id: toastId });
        vezgoState.setProcessingState(ConnectionProcessingState.IDLE);
        return;
      }

      // Build the connect URL for Vezgo (now including the token)
      const connectUrl = buildVezgoConnectUrl(
        vezgoConfig.connectUrl,
        vezgoConfig.clientId,
        vezgoConfig.redirectUri,
        token, // Pass the token to the URL builder
        undefined, // Provider optional
        state
      );

      logger.info('useVezgoConnectActions', 'Redirecting to Vezgo connect', {
        url: connectUrl,
        provider: vezgoState.provider,
        redirectUri: vezgoConfig.redirectUri,
        stateParam: state,
        tokenSample: token.slice(0, 8) + '...',
        tokenIncluded: connectUrl.includes('token=')
      });

      // Update toast and add delay to ensure user sees the message
      toast.loading("Redirecting to provider...", { id: toastId, duration: 2000 });
      setTimeout(() => {
        window.location.href = connectUrl;
      }, 750);
    } catch (error) {
      handleVezgoError(error, {
        provider: vezgoState.providerDisplayName,
        operation: 'Connection',
        setProcessingState: vezgoState.setProcessingState,
        showToast: true
      });

      toast.error("Connection failed", { id: toastId });
      vezgoState.setProcessingState(ConnectionProcessingState.IDLE);
    }
  }, [vezgoState]);

  const handleRefresh = useCallback(async () => {
    if (vezgoState.isRefreshInProgress) {
      toast.info('Refresh already in progress');
      return;
    }

    logger.info('useVezgoConnectActions', 'Initiating refresh action');

    vezgoState.startProcessing();
    vezgoState.setProcessingState(ConnectionProcessingState.LOADING_ACCOUNTS);

    const toastId = toast.loading("Refreshing connection...");

    try {
      await vezgoState.refreshConnection();
      toast.success("Successfully refreshed", { id: toastId });
      vezgoState.setProcessingState(ConnectionProcessingState.COMPLETE);
    } catch (error) {
      handleVezgoError(error, {
        provider: vezgoState.providerDisplayName,
        operation: 'Refresh',
        setProcessingState: vezgoState.setProcessingState,
        showToast: true
      });

      toast.error("Refresh failed", { id: toastId });
      vezgoState.setProcessingState(ConnectionProcessingState.IDLE);
    }
  }, [vezgoState]);

  const handleDisconnect = useCallback(async () => {
    logger.info('useVezgoConnectActions', 'Initiating disconnect action');

    const toastId = toast.loading("Disconnecting...");

    try {
      await vezgoState.disconnect();
      toast.success("Successfully disconnected", { id: toastId });
    } catch (error) {
      handleVezgoError(error, {
        provider: vezgoState.providerDisplayName,
        operation: 'Disconnect',
        showToast: true
      });

      toast.error("Disconnect failed", { id: toastId });
    }
  }, [vezgoState]);

  /**
   * Debug/test token helper—calls tokenService.getToken and displays UI toast
   */
  const testToken = useCallback(async () => {
    const toastId = toast.loading("Testing token retrieval...");
    try {
      const token = await tokenService.getToken(vezgoConfig.clientId);
      if (token) {
        toast.success(`Token acquired (length=${token.length})`, { id: toastId });
        logger.info('useVezgoConnectActions', 'Test Token success', { token: token.substring(0, 5) + '...' });
      } else {
        toast.error("No token received!", { id: toastId });
        logger.error('useVezgoConnectActions', 'Test Token failed: No token');
      }
    } catch (err) {
      toast.error("Token error!", { id: toastId });
      logger.error('useVezgoConnectActions', 'Token test exception', { err });
    }
  }, []);

  return {
    handleConnect,
    handleRefresh,
    handleDisconnect,
    testToken // Exposed only for dev-debug
  };
}
